import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, MapPin, AlertTriangle, X, Plus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function StaffAttendancePanel({ can, isSuper }) {
  const [date, setDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [showManual, setShowManual] = useState(null); // staff object or null
  const [showQR, setShowQR] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/staff-attendance/feed?date=${date}`);
      setRows(data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const stats = useMemo(() => {
    let inCount = 0, lateCount = 0, absent = 0, outCount = 0;
    rows.forEach((r) => {
      if (r.in) {
        inCount++;
        if (r.in.status === "late") lateCount++;
      } else {
        absent++;
      }
      if (r.out) outCount++;
    });
    return { inCount, lateCount, absent, outCount };
  }, [rows]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-3xl text-[#0F1B47] font-medium">Staff Clock-ins</h2>
          <p className="text-sm text-[#718096]">Live feed of QR-based attendance. Use manual override if a staff forgets their phone.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="text-sm font-semibold text-[#0F1B47] bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-full px-4 py-2"
            data-testid="staff-att-date"
          />
          <button onClick={load} className="text-sm font-semibold text-[#0F1B47] bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-full px-3 py-2 inline-flex items-center gap-1 hover:border-[#0F1B47]/30" data-testid="staff-att-refresh">
            <RefreshCw size={13} />
          </button>
          <button onClick={() => setShowQR(true)} className="text-sm font-semibold text-[#0F1B47] bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-full px-4 py-2 inline-flex items-center gap-1 hover:border-[#0F1B47]/30" data-testid="staff-att-qr">
            QR poster
          </button>
          {(isSuper || can("export_attendance")) && (
            <button onClick={() => setShowExport(true)} className="text-sm font-semibold bg-[#0F1B47] text-[#FDFDF9] rounded-full px-4 py-2 inline-flex items-center gap-1.5 hover:bg-[#1a2b6b]" data-testid="staff-att-export-btn">
              <Download size={14} /> Export
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile k="Clocked in" v={stats.inCount} accent="ok" />
        <StatTile k="Late" v={stats.lateCount} accent={stats.lateCount > 0 ? "warn" : "muted"} />
        <StatTile k="Absent" v={stats.absent} accent={stats.absent > 0 ? "bad" : "muted"} />
        <StatTile k="Clocked out" v={stats.outCount} accent="muted" />
      </div>

      <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F0] text-[#0F1B47]">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Staff</th>
              <th className="text-left px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Class(es)</th>
              <th className="text-left px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Clock-in</th>
              <th className="text-left px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Clock-out</th>
              <th className="text-left px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Distance</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold tracking-[0.16em] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center text-[#A0AEC0] py-8 text-xs">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#A0AEC0] py-8 text-xs">No staff to show.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-[#0F1B47]/6 hover:bg-[#F5F5F0]/40" data-testid={`staff-att-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0F1B47]">{r.name || r.email}</div>
                  <div className="text-[11px] text-[#718096]">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-[12px] text-[#4A5568]">
                  {(r.classes || []).join(", ") || <span className="text-[#A0AEC0]">—</span>}
                </td>
                <td className="px-4 py-3"><TimeCell record={r.in} /></td>
                <td className="px-4 py-3"><TimeCell record={r.out} /></td>
                <td className="px-4 py-3 text-[12px]">
                  {r.in?.distance_m != null ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E6FFFA] text-[#2F855A] font-bold text-[10px]">
                      <MapPin size={10} /> {Math.round(r.in.distance_m)} m
                    </span>
                  ) : r.in?.manual ? (
                    <span className="text-[10px] text-[#B7791F] font-bold uppercase tracking-wider">Manual</span>
                  ) : (
                    <span className="text-[#A0AEC0]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {can("manage_attendance") && (
                    <button onClick={() => setShowManual(r)} className="text-[11px] font-semibold text-[#0F1B47] underline" data-testid={`staff-att-manual-${r.id}`}>
                      Manual mark
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showExport && <ExportDialog onClose={() => setShowExport(false)} />}
      {showManual && <ManualDialog staff={showManual} date={date} onClose={() => setShowManual(null)} onSaved={() => { setShowManual(null); load(); }} />}
      {showQR && <QRPosterDialog onClose={() => setShowQR(false)} />}
    </div>
  );
}

function TimeCell({ record }) {
  if (!record) return <span className="text-[#A0AEC0]">—</span>;
  const t = new Date(record.timestamp);
  const time = t.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const cls = record.status === "late" ? "bg-[#FFF5B7] text-[#B7791F]" : "bg-[#E6FFFA] text-[#2F855A]";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[#0F1B47] font-bold">{time}</span>
      <span className={`text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${cls}`}>
        {record.status === "late" ? "Late" : "On time"}
      </span>
      {record.manual && <span className="text-[9px] text-[#A0AEC0] font-bold uppercase tracking-[0.12em]">Manual</span>}
    </div>
  );
}

function StatTile({ k, v, accent }) {
  const accents = {
    ok: "bg-[#E6FFFA] text-[#2F855A]",
    warn: "bg-[#FFF5B7] text-[#B7791F]",
    bad: "bg-[#FED7D7] text-[#C53030]",
    muted: "bg-[#F5F5F0] text-[#0F1B47]",
  };
  return (
    <div className={`rounded-2xl p-4 ${accents[accent] || accents.muted}`}>
      <div className="text-[10px] font-bold tracking-[0.16em] uppercase opacity-80">{k}</div>
      <div className="text-2xl font-display font-medium mt-0.5">{v}</div>
    </div>
  );
}

function ExportDialog({ onClose }) {
  const today = todayIso();
  const firstOfMonth = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await adminApi.get(`/admin/reports/staff-attendance.xlsx?date_from=${from}&date_to=${to}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `naavi-staff-attendance-${from}-to-${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Report downloaded");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const setPreset = (mode) => {
    const d = new Date();
    if (mode === "today") { const t = todayIso(); setFrom(t); setTo(t); }
    if (mode === "week") {
      const day = new Date(d); day.setDate(d.getDate() - 6);
      setFrom(day.toISOString().slice(0, 10)); setTo(todayIso());
    }
    if (mode === "month") {
      setFrom(todayIso().slice(0, 8) + "01"); setTo(todayIso());
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="staff-att-export-dialog">
        <div className="px-6 pt-5 pb-2 flex items-center justify-between">
          <h3 className="font-display text-2xl text-[#0F1B47] font-medium">Export staff attendance</h3>
          <button onClick={onClose} className="text-[#0F1B47] p-1.5 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="px-6 pb-6 space-y-4">
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => setPreset("today")} className="text-xs font-semibold bg-[#F5F5F0] text-[#0F1B47] rounded-full px-3 py-1.5">Today</button>
            <button type="button" onClick={() => setPreset("week")} className="text-xs font-semibold bg-[#F5F5F0] text-[#0F1B47] rounded-full px-3 py-1.5">Last 7 days</button>
            <button type="button" onClick={() => setPreset("month")} className="text-xs font-semibold bg-[#F5F5F0] text-[#0F1B47] rounded-full px-3 py-1.5">This month</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="staff-att-export-from" /></Field>
            <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="staff-att-export-to" /></Field>
          </div>
          <div className="text-[11px] text-[#718096]">
            Excel workbook with three sheets: Summary (per-staff totals), Daily detail (every scan), Settings snapshot (geofence/time-windows at the time of export).
          </div>
          <button
            type="button"
            onClick={download}
            disabled={downloading}
            className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="staff-att-export-download"
          >
            <Download size={15} /> {downloading ? "Building report…" : "Download .xlsx"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualDialog({ staff, date, onClose, onSaved }) {
  const [type, setType] = useState("in");
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!time) return toast.error("Pick a time");
    setSaving(true);
    try {
      await adminApi.post("/admin/staff-attendance/manual", { staff_id: staff.id, type, date, time, note });
      toast.success(`Marked ${type === "in" ? "clock-in" : "clock-out"} manually`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="staff-att-manual-dialog">
        <div className="px-6 pt-5 pb-2 flex items-center justify-between">
          <h3 className="font-display text-2xl text-[#0F1B47] font-medium">Manual mark · {staff.name}</h3>
          <button onClick={onClose} className="text-[#0F1B47] p-1.5 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="px-6 pb-6 space-y-4">
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl px-3 py-2 text-[11px] flex gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-none" />
            Use this only when a staff forgot their phone or GPS failed. Existing entry for this slot will be overwritten.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">Slot</label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8" data-testid="staff-att-manual-type">
                <option value="in">Clock-in</option>
                <option value="out">Clock-out</option>
              </select>
            </div>
            <Field label="Time">
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="staff-att-manual-time" />
            </Field>
          </div>
          <Field label="Note (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. forgot phone" data-testid="staff-att-manual-note" />
          </Field>
          <div className="text-[11px] text-[#718096]">Date: {date}</div>
          <button onClick={submit} disabled={saving} className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full py-3 font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="staff-att-manual-save">
            <Plus size={15} /> {saving ? "Saving…" : "Save attendance"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QRPosterDialog({ onClose }) {
  const url = `${window.location.origin}/staff/clock`;
  const print = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="staff-att-qr-dialog">
        <div className="px-6 pt-5 pb-2 flex items-center justify-between print:hidden">
          <h3 className="font-display text-2xl text-[#0F1B47] font-medium">Wall QR poster</h3>
          <button onClick={onClose} className="text-[#0F1B47] p-1.5 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div id="qr-print-area" className="px-8 pb-8 text-center">
          <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-1">Naavi Early Years</div>
          <div className="font-display text-3xl text-[#0F1B47] font-medium mb-2">Staff clock-in</div>
          <p className="text-sm text-[#718096] mb-5">Scan with your phone camera. Stand within the campus geofence.</p>
          <div className="inline-block bg-[#FDFDF9] border border-[#0F1B47]/8 rounded-3xl p-5">
            <QRCodeSVG value={url} size={220} bgColor="#FDFDF9" fgColor="#0F1B47" includeMargin={false} />
          </div>
          <div className="mt-4 text-[10px] text-[#718096] break-all">{url}</div>
          <button onClick={print} className="mt-5 inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 text-sm font-bold print:hidden" data-testid="staff-att-qr-print">
            Print poster
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">{label}</label>
      {React.cloneElement(children, { className: "w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8 focus:outline-none focus:border-[#0F1B47]/30" })}
    </div>
  );
}
